import classNames from 'classnames';
import { ComponentProps, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import { getWeatherIcon } from '@alltrails/denali/components/WeatherIcon';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import { DropdownMenuOption } from '@alltrails/denali/types';
import styles from './DaySelect.module.scss';

type DaySelectProps = {
  days: { tempHigh?: number; tempLow?: number; utcMs: number; weatherIconCode?: number }[];
  onChange: (utcMs: number) => void;
  selectedUTCMs: number; // Any timezone offsets should already be applied so that we can treat the date as UTC
  showFullWeekdayName?: boolean;
  size?: 'sm' | 'md';
} & Pick<ComponentProps<typeof DropdownMenu>, 'placement'>;

const formatWeekday = (intl: IntlShape, utcMs: number, format: 'long' | 'short') =>
  intl.formatDate(new Date(utcMs), { weekday: format, timeZone: 'UTC' });

const formatTemp = (intl: IntlShape, value: number) =>
  intl.formatNumber(value, { style: 'unit', unit: 'degree', unitDisplay: 'narrow', maximumFractionDigits: 0 });

// https://www.figma.com/design/6lD2rWP01sgcuqzuhG6EXK/Conditions-%2F-Overlays-2.0-Web-(Handoff)?node-id=1833-453726&m=dev
const DaySelect = ({ days, onChange, placement, selectedUTCMs, showFullWeekdayName = false, size = 'md' }: DaySelectProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const options: DropdownMenuOption[] = useMemo(
    () =>
      days.map(({ tempHigh, tempLow, utcMs, weatherIconCode }) => {
        const IconComponent = weatherIconCode ? getWeatherIcon(weatherIconCode, true) : undefined;
        const selected = selectedUTCMs === utcMs;
        const weekday = formatWeekday(intl, utcMs, 'long');
        const textContent = (
          <div className={styles.dropdownOption}>
            <Typography variant={selected ? 'text100bold' : 'text100'}>{weekday}</Typography>
            {tempHigh !== undefined && tempLow !== undefined ? (
              <Typography color="secondary" variant="text100">
                {formatTemp(intl, tempHigh)} / {formatTemp(intl, tempLow)}
              </Typography>
            ) : null}
          </div>
        );
        return {
          leadingIcon: IconComponent ? { icon: { Component: IconComponent } } : undefined,
          onClick: () => {
            if (selectedUTCMs !== utcMs) {
              onChange(utcMs);
            }
            setIsOpen(false);
          },
          selected,
          testId: `condition-day-select-${weekday}`,
          title: textContent
        };
      }),
    [days, intl, onChange, selectedUTCMs]
  );

  return (
    <DropdownMenu
      anchor={
        <button
          className={classNames(styles.anchor, styles[size], { [styles.dropdownOpen]: isOpen })}
          onClick={() => setIsOpen(!isOpen)}
          title={formatWeekday(intl, selectedUTCMs, 'long')}
        >
          {formatWeekday(intl, selectedUTCMs, showFullWeekdayName ? 'long' : 'short')}
          <ChevronSm orientation={isOpen ? 'up' : 'down'} size="xs" />
        </button>
      }
      className={styles.dropdown}
      isOpen={isOpen}
      onCloseRequest={() => setIsOpen(false)}
      options={options}
      placement={placement}
    />
  );
};

export default DaySelect;
